<template>
  <div class="relative flex flex-col overflow-hidden" style="min-height: 400px; background: #f1f0ed;">

    <!-- ══════════════════════════════════════════
         MOBILE  (< lg)
         ══════════════════════════════════════════ -->
    <div class="flex flex-col flex-1 lg:hidden" style="position: relative;">

      <!-- Chargement -->
      <div v-if="loadingAddressDevices" class="flex-1 flex flex-col items-center justify-center gap-4 py-16">
        <i class="fas fa-spinner fa-spin text-5xl" style="color: #f97316;"></i>
        <p class="text-xl font-bold" style="color: #888;">Chargement...</p>
      </div>

      <template v-else>

        <!-- ── État vide : grille directe ── -->
        <div v-if="addedDevices.length === 0" class="flex flex-col flex-1 overflow-y-auto" style="padding: 20px 14px;">
          <p class="font-bold" style="font-size: 20px; color: #1a1a1a; margin-bottom: 4px;">Quel appareil ?</p>
          <p style="font-size: 14px; color: #888; margin-bottom: 16px;">Choisissez un type pour commencer</p>
          <div v-if="loadingCategories" class="flex items-center gap-3 py-8">
            <i class="fas fa-spinner fa-spin" style="color: #f97316; font-size: 20px;"></i>
            <span style="color: #888; font-size: 15px;">Chargement…</span>
          </div>
          <div v-else style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px;">
            <button
              v-for="cat in categories" :key="cat.id"
              @click="addDeviceAndOpenModal(cat)"
              style="background: #fff; border-radius: 16px; border: 1.5px solid #e8e6e1; padding: 18px 12px; display: flex; flex-direction: column; align-items: center; gap: 10px; cursor: pointer;">
              <div style="width: 50px; height: 50px; border-radius: 14px; background: #f4f3f0; display: flex; align-items: center; justify-content: center;">
                <img v-if="cat.icon" :src="cat.icon" :alt="cat.label" style="width: 26px; height: 26px; object-fit: contain;" />
                <i v-else class="fas fa-fire" style="font-size: 22px; color: #555;"></i>
              </div>
              <span style="font-size: 13px; font-weight: 600; color: #1a1a1a; text-align: center; line-height: 1.3;">{{ cat.label }}</span>
            </button>
          </div>
        </div>

        <!-- ── Liste des appareils ── -->
        <template v-else>

          <!-- Bannière appareils trouvés -->
          <div v-if="addressDevicesLoaded && addedDevices.some(d => d.appareil_id)"
            style="margin: 12px 14px 0; display: flex; align-items: center; gap: 10px; background: #eff6ff; border: 1.5px solid #93c5fd; border-radius: 12px; padding: 10px 14px;">
            <i class="fas fa-circle-info" style="font-size: 15px; color: #2563eb; flex-shrink: 0;"></i>
            <span style="font-size: 13px; font-weight: 600; color: #1d4ed8;">
              {{ addedDevices.filter(d => d.appareil_id).length }} appareil(s) trouvé(s) à cette adresse
            </span>
          </div>

          <!-- Liste -->
          <div class="flex-1 overflow-y-auto" style="padding: 12px 14px 90px; display: flex; flex-direction: column; gap: 8px;">
            <button
              v-for="(device, i) in addedDevices"
              :key="device._uid"
              @click="openInterventionModal(i)"
              style="width: 100%; background: #fff; border-radius: 18px; border: 1.5px solid #e8e6e1; padding: 14px 16px; display: flex; align-items: center; gap: 14px; text-align: left; cursor: pointer;"
              :style="{
                borderColor: (device.chantierInterventions && device.chantierInterventions.length) ? '#93c5fd'
                  : device.intervention_type_id ? '#fdba74'
                  : '#e8e6e1'
              }">

              <div style="width: 50px; height: 50px; border-radius: 14px; background: #f4f3f0; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                <img v-if="device.icon" :src="device.icon" :alt="device.type" style="width: 26px; height: 26px; object-fit: contain;" />
                <i v-else class="fas fa-fire" style="font-size: 22px; color: #555;"></i>
              </div>

              <div style="flex: 1; min-width: 0;">
                <p style="font-size: 16px; font-weight: 600; color: #1a1a1a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ device.type }}</p>
                <p v-if="device.marque || device.modele" style="font-size: 13px; color: #888; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                  {{ [device.marque, device.modele].filter(Boolean).join(' · ') }}
                </p>

                <!-- Déjà planifiée -->
                <template v-if="device.chantierInterventions && device.chantierInterventions.length">
                  <p v-for="iv in device.chantierInterventions" :key="iv.id" style="font-size: 13px; font-weight: 600; margin-top: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                    :style="{ color: iv.statut && iv.statut.couleur ? iv.statut.couleur : '#2563eb' }">
                    ✓ {{ (iv.intervention_type && iv.intervention_type.nom) || '—' }}
                  </p>
                </template>

                <!-- Configurée -->
                <p v-else-if="device.intervention_type_id" style="font-size: 13px; font-weight: 600; color: #ea580c; margin-top: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                  {{ (device.interventionTypes.find(t => t.id === device.intervention_type_id) || {}).nom }}
                  <span v-if="device.duree_estimee" style="color: #f97316;"> · {{ formatDuration(device.duree_estimee) }}</span>
                </p>

                <!-- À configurer -->
                <p v-else style="font-size: 13px; color: #bbb; margin-top: 3px;">Toucher pour choisir →</p>
              </div>

              <div style="width: 26px; height: 26px; border-radius: 50%; border: 1.5px solid #d4d2cc; display: flex; align-items: center; justify-content: center; flex-shrink: 0;"
                :style="(device.intervention_type_id || (device.chantierInterventions && device.chantierInterventions.length))
                  ? 'background: #f97316; border-color: #f97316;'
                  : ''">
                <i v-if="device.intervention_type_id || (device.chantierInterventions && device.chantierInterventions.length)"
                  class="fas fa-check" style="font-size: 11px; color: #fff;"></i>
              </div>
            </button>
          </div>

          <!-- Bouton Ajouter -->
          <div v-if="!readonly" style="position: absolute; bottom: 0; left: 0; right: 0; padding: 12px 14px 20px; background: linear-gradient(to bottom, transparent, #f1f0ed 40%);">
            <button @click="$modal.show('mobile-add-device')"
              style="width: 100%; height: 54px; background: #f97316; color: #fff; border: none; border-radius: 16px; font-size: 16px; font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 10px; cursor: pointer;">
              <i class="fas fa-plus" style="font-size: 18px;"></i>
              Ajouter un appareil
            </button>
          </div>
        </template>

      </template>
    </div>
    <!-- fin mobile -->

    <!-- ══════════════════════════════════════════
         DESKTOP  (lg+)  —  sidebar + panneau
         ══════════════════════════════════════════ -->
    <div class="hidden lg:flex flex-col flex-1" style="min-height: 460px">
      <div class="flex flex-1 overflow-hidden" style="min-height: 390px">

        <!-- Sidebar -->
        <div class="flex-shrink-0 flex flex-col bg-slate-50 border-r border-slate-100"
          style="width: 216px" @click.stop>

          <div class="flex items-center justify-between px-3 py-2.5 border-b border-slate-100 flex-shrink-0">
            <div class="flex items-center gap-2">
              <span class="text-xs font-black text-slate-400 uppercase tracking-widest">Appareils</span>
              <span v-if="addedDevices.length"
                class="text-xs font-bold text-slate-500 bg-slate-200 px-1.5 py-0.5 rounded-full leading-none">
                {{ addedDevices.length }}
              </span>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto">
            <div v-if="loadingAddressDevices" class="flex flex-col items-center gap-2 py-10 text-center">
              <i class="fas fa-spinner fa-spin text-slate-300 text-lg"></i>
              <p class="text-xs text-slate-400">Chargement du parc…</p>
            </div>

            <div v-else-if="addedDevices.length === 0" class="p-2.5">
              <template v-if="!readonly">
                <p class="text-xs text-slate-400 text-center mb-2">Ajoutez un appareil</p>
                <div v-if="!loadingCategories" class="grid grid-cols-2 gap-1">
                  <button v-for="cat in categories" :key="cat.id"
                    @click.stop="addDevice(cat)"
                    class="group flex flex-col items-center gap-1 py-2 px-1 rounded-xl border border-dashed border-slate-200 hover:border-orange-400 hover:bg-orange-50 transition-all">
                    <div class="w-7 h-7 rounded-lg bg-white flex items-center justify-center">
                      <img v-if="cat.icon" :src="cat.icon" :alt="cat.label" class="w-4 h-4 object-contain opacity-60 group-hover:opacity-100" />
                      <i v-else class="fas fa-fire text-slate-300 text-xs"></i>
                    </div>
                    <span class="text-center font-black text-slate-400 group-hover:text-slate-700 leading-tight" style="font-size:9px">{{ cat.label }}</span>
                  </button>
                </div>
              </template>
              <div v-else class="flex flex-col items-center gap-2 py-8 text-center">
                <i class="fas fa-box-open text-slate-200 text-2xl"></i>
                <p class="text-xs text-slate-400">Aucun appareil</p>
              </div>
            </div>

            <div v-else>
              <div v-if="addressDevicesLoaded && addedDevices.some(d => d.appareil_id)"
                class="mx-2.5 mt-2 mb-1 flex items-center gap-1.5 bg-blue-50 border border-blue-100 px-2 py-1.5 rounded-lg">
                <i class="fas fa-circle-info text-blue-400" style="font-size:9px"></i>
                <p class="text-blue-600 font-black text-xs">{{ addedDevices.filter(d => d.appareil_id).length }} trouvé(s)</p>
              </div>

              <div v-if="!readonly" class="relative p-2">
                <button @click.stop="showAddDropdown = !showAddDropdown"
                  :disabled="loadingCategories"
                  class="w-full flex items-center justify-center h-9 rounded-xl border transition-all text-xs font-black"
                  :class="showAddDropdown
                    ? 'bg-slate-800 border-slate-800 text-white'
                    : 'text-slate-500 border-slate-200 hover:bg-slate-800 hover:border-slate-800 hover:text-white'">
                  <i class="fas fa-plus mr-1.5"></i> Ajouter
                </button>
                <div v-if="showAddDropdown"
                  class="absolute right-0 top-11 w-52 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden z-30 py-1"
                  @click.stop>
                  <button v-for="cat in categories" :key="cat.id"
                    @click.stop="addDevice(cat); showAddDropdown = false"
                    class="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors">
                    <div class="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <img v-if="cat.icon" :src="cat.icon" :alt="cat.label" class="w-4 h-4 object-contain" />
                      <i v-else class="fas fa-fire text-slate-400 text-xs"></i>
                    </div>
                    {{ cat.label }}
                  </button>
                </div>
              </div>

              <div v-for="(device, i) in addedDevices" :key="device._uid" class="relative">
                <button @click.stop="selectDevice(i)"
                  class="relative w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors"
                  :class="selectedDeviceIndex === i ? 'bg-white' : 'hover:bg-white/70'">
                  <div class="absolute left-0 top-2 bottom-2 w-0.5 rounded-r transition-all"
                    :class="selectedDeviceIndex === i ? 'bg-orange-500' : 'bg-transparent'"></div>
                  <div class="flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center"
                    :class="(device.chantierInterventions && device.chantierInterventions.length) || device.intervention_type_id
                      ? 'border-orange-500 bg-orange-500' : 'border-slate-300 bg-white'">
                    <i v-if="(device.chantierInterventions && device.chantierInterventions.length) || device.intervention_type_id"
                      class="fas fa-check text-white" style="font-size:7px"></i>
                    <i v-else-if="device.loadingTypes" class="fas fa-spinner fa-spin text-slate-300" style="font-size:7px"></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs font-black truncate"
                      :class="device.chantierInterventions && device.chantierInterventions.length ? 'text-slate-400' : 'text-slate-700'">
                      {{ device.type }}
                    </p>
                    <template v-if="device.chantierInterventions && device.chantierInterventions.length">
                      <p v-for="iv in device.chantierInterventions" :key="iv.id" class="text-xs truncate mt-0.5 flex items-center gap-1">
                        <span class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          :style="{ background: iv.statut && iv.statut.couleur ? iv.statut.couleur : '#d1d5db' }"></span>
                        <span class="truncate font-bold" :style="{ color: iv.statut && iv.statut.couleur ? iv.statut.couleur : '#6b7280' }">
                          {{ (iv.intervention_type && iv.intervention_type.nom) || '—' }}
                        </span>
                      </p>
                    </template>
                    <template v-else>
                      <p class="text-xs truncate mt-0.5" :class="device.intervention_type_id ? 'text-orange-500 font-bold' : 'text-slate-400'">
                        <template v-if="device.intervention_type_id">
                          {{ (device.interventionTypes.find(t => t.id === device.intervention_type_id) || {}).nom }}
                        </template>
                        <template v-else>À configurer</template>
                      </p>
                    </template>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Panneau principal -->
        <div class="flex-1 flex flex-col overflow-hidden bg-white" @click="showAddDropdown = false">
          <div v-if="selectedDeviceIndex === null || loadingAddressDevices"
            class="flex flex-col items-center justify-center gap-4 flex-1 text-center px-10">
            <div class="w-16 h-16 rounded-2xl bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center">
              <i class="fas fa-hand-pointer text-3xl text-slate-300"></i>
            </div>
            <p class="text-sm font-black text-slate-600">Sélectionnez un appareil</p>
          </div>

          <div v-else-if="selectedDevice" class="flex flex-col flex-1 overflow-hidden">
            <div class="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100 flex-shrink-0">
              <div class="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                <img v-if="selectedDevice.icon" :src="selectedDevice.icon" :alt="selectedDevice.type" class="w-5 h-5 object-contain opacity-80" />
                <i v-else class="fas fa-fire text-slate-400"></i>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <h3 class="text-sm font-black text-slate-900">{{ selectedDevice.type }}</h3>
                  <span v-if="!selectedDevice.appareil_id" class="text-xs font-black text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded-md">NOUVEAU</span>
                  <span v-if="selectedDevice.intervention_type_id"
                    class="inline-flex items-center gap-1 text-xs font-black text-orange-600 bg-orange-50 border border-orange-100 px-2 py-0.5 rounded-full">
                    <i class="fas fa-check" style="font-size:8px"></i>
                    {{ (selectedDevice.interventionTypes.find(t => t.id === selectedDevice.intervention_type_id) || {}).nom }}
                  </span>
                </div>
                <p class="text-xs text-slate-400 mt-1 truncate">
                  {{ [selectedDevice.marque, selectedDevice.modele, selectedDevice.emplacement].filter(Boolean).join(' · ')
                    || (selectedDevice.appareil_id ? 'Réf. #' + selectedDevice.appareil_id : 'Non encore enregistré') }}
                </p>
              </div>
              <button @click.stop="confirmDeleteDevice(selectedDeviceIndex)" :disabled="loadingDeleteDevice"
                class="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-red-400 hover:border-red-200 hover:bg-red-50 transition-all disabled:opacity-40">
                <i v-if="loadingDeleteDevice" class="fas fa-spinner fa-spin" style="font-size:10px"></i>
                <i v-else class="fas fa-trash-alt" style="font-size:10px"></i>
              </button>
            </div>

            <div class="flex divide-x divide-slate-100 flex-1 overflow-hidden">
              <div v-if="!readonly" class="flex-1 overflow-y-auto px-4 py-4">
                <p class="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Que souhaitez-vous faire ?</p>

                <div v-if="selectedDevice.chantierInterventions && selectedDevice.chantierInterventions.length" class="flex flex-col gap-2">
                  <div v-for="iv in selectedDevice.chantierInterventions" :key="iv.id"
                    class="flex items-center gap-3 px-4 py-3 bg-blue-50 border-2 border-blue-100 rounded-xl">
                    <i class="fas fa-calendar-check text-blue-500 flex-shrink-0"></i>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-black text-slate-900 truncate">{{ (iv.intervention_type && iv.intervention_type.nom) || '—' }}</p>
                      <p class="text-xs font-bold mt-0.5" :style="{ color: iv.statut && iv.statut.couleur ? iv.statut.couleur : '#6b7280' }">
                        {{ iv.statut && iv.statut.libelle }}
                      </p>
                    </div>
                  </div>
                  <p class="text-xs text-slate-400 text-center mt-1"><i class="fas fa-lock mr-1"></i>Déjà prévue sur ce chantier</p>
                </div>

                <div v-else-if="selectedDevice.loadingTypes" class="flex items-center gap-2 py-4 text-xs text-slate-400">
                  <i class="fas fa-spinner fa-spin"></i> Chargement…
                </div>

                <template v-else>
                  <template v-if="selectedDevice.intervention_type_id">
                    <div class="flex items-center gap-3 px-4 py-3 bg-orange-50 border-2 border-orange-200 rounded-xl">
                      <i class="fas fa-wrench text-orange-500 flex-shrink-0"></i>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-black text-slate-900 truncate">
                          {{ (selectedDevice.interventionTypes.find(t => t.id === selectedDevice.intervention_type_id) || {}).nom }}
                        </p>
                        <p class="text-xs text-orange-500 font-bold mt-0.5">{{ formatDuration(selectedDevice.duree_estimee) || '—' }}</p>
                      </div>
                      <button @click="setInterventionType(selectedDeviceIndex, null)" class="text-xs text-slate-400 hover:text-orange-500 font-black">Modifier</button>
                    </div>
                    <div class="mt-4 pt-4 border-t border-slate-100">
                      <p class="text-xs font-black text-slate-400 uppercase tracking-widest mb-2.5">Durée estimée</p>
                      <div class="flex items-center gap-2">
                        <div class="flex items-center border-2 border-slate-200 rounded-xl overflow-hidden focus-within:border-orange-400 transition-colors">
                          <input type="number" min="0" max="23"
                            :value="Math.floor((selectedDevice.duree_estimee || 60) / 60)"
                            @change="setDureeEstimeeHHMM(selectedDeviceIndex, $event.target.value, (selectedDevice.duree_estimee || 60) % 60)"
                            class="w-20 px-3 py-2 text-sm font-black text-slate-900 text-center focus:outline-none bg-transparent" />
                          <span class="pr-3 text-xs text-slate-400 font-bold">h</span>
                        </div>
                        <div class="flex items-center border-2 border-slate-200 rounded-xl overflow-hidden focus-within:border-orange-400 transition-colors">
                          <input type="number" min="0" max="59" step="15"
                            :value="(selectedDevice.duree_estimee || 60) % 60"
                            @change="setDureeEstimeeHHMM(selectedDeviceIndex, Math.floor((selectedDevice.duree_estimee || 60) / 60), $event.target.value)"
                            class="w-20 px-3 py-2 text-sm font-black text-slate-900 text-center focus:outline-none bg-transparent" />
                          <span class="pr-3 text-xs text-slate-400 font-bold">min</span>
                        </div>
                        <button v-if="selectedDevice.duree_estimee !== getDefaultDuree(selectedDevice)"
                          @click="resetDureeEstimee(selectedDeviceIndex)"
                          class="text-xs text-orange-400 hover:text-orange-600 font-black">Réinit.</button>
                      </div>
                    </div>
                  </template>
                  <div v-else class="flex flex-col gap-1">
                    <button v-for="t in selectedDevice.interventionTypes" :key="t.id"
                      @click="setInterventionType(selectedDeviceIndex, t.id)"
                      class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-transparent hover:bg-orange-50 hover:border-orange-100 group text-left transition-all">
                      <div class="w-4 h-4 rounded-full border-2 border-slate-300 flex-shrink-0 group-hover:border-orange-400 transition-colors"></div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm text-slate-700 group-hover:text-slate-900 font-bold truncate">{{ t.nom }}</p>
                        <p v-if="t.time_min" class="text-xs text-slate-400 mt-0.5"><i class="fas fa-clock mr-1" style="font-size:9px"></i>{{ formatDuration(t.time_min) }}</p>
                      </div>
                      <i class="fas fa-chevron-right text-slate-200 text-xs group-hover:text-orange-300 flex-shrink-0"></i>
                    </button>
                  </div>
                </template>
              </div>

              <div class="overflow-y-auto px-4 py-4 flex-shrink-0" :style="readonly ? 'width:100%' : 'width:40%'">
                <p class="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Historique</p>
                <div v-if="!selectedDevice.appareil_id" class="flex flex-col items-center gap-2 py-6 text-center">
                  <i class="fas fa-seedling text-slate-200 text-3xl"></i>
                  <p class="text-xs text-slate-400">Nouvel appareil</p>
                </div>
                <div v-else-if="loadingHistory" class="flex items-center gap-2 py-4 text-xs text-slate-400">
                  <i class="fas fa-spinner fa-spin"></i> Chargement…
                </div>
                <div v-else-if="deviceHistory.length === 0" class="flex flex-col items-center gap-2 py-6 text-center">
                  <i class="fas fa-clock text-slate-200 text-3xl"></i>
                  <p class="text-xs text-slate-400">Aucune intervention</p>
                </div>
                <div v-else class="relative">
                  <div class="absolute left-2.5 top-3 bottom-3 w-px bg-slate-100"></div>
                  <div v-for="h in deviceHistory" :key="h.id" class="flex items-start gap-3 pb-3.5 last:pb-0 relative">
                    <div class="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 z-10 mt-0.5 bg-white"
                      :style="{ border: '1.5px solid ' + (h.statut && h.statut.couleur ? h.statut.couleur : '#e5e7eb') }">
                      <div class="w-1.5 h-1.5 rounded-full" :style="{ background: h.statut && h.statut.couleur ? h.statut.couleur : '#d1d5db' }"></div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-xs font-black text-slate-800 truncate">
                        {{ (h.interventionType && h.interventionType.nom) || (h.intervention_type && h.intervention_type.nom) || '—' }}
                      </p>
                      <p class="text-xs text-slate-400 mt-0.5">{{ formatHistoryDate(h.date_intervention) }}</p>
                      <span v-if="h.statut" class="inline-block mt-1 text-xs font-black px-1.5 py-0.5 rounded-md"
                        :style="{ background: (h.statut.couleur || '#6b7280') + '15', color: h.statut.couleur || '#6b7280' }">
                        {{ h.statut.libelle }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Récap bas desktop -->
          <transition name="recap-slide">
            <div v-if="!readonly && addedDevices.some(d => d.intervention_type_id)"
              class="flex-shrink-0 border-t border-slate-100 bg-white px-4 py-2">
              <div class="flex items-center gap-3">
                <p class="text-xs font-black text-slate-900 flex-shrink-0 whitespace-nowrap">
                  {{ addedDevices.filter(d => d.intervention_type_id).length }}
                  <span class="font-normal text-slate-400">/ {{ addedDevices.length }} configuré(s)</span>
                </p>
                <div class="w-px h-4 bg-slate-200 flex-shrink-0"></div>
                <div class="flex gap-1.5 overflow-x-auto flex-1" style="scrollbar-width:none">
                  <div v-for="device in addedDevices.filter(d => d.intervention_type_id)" :key="device._uid"
                    class="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-2 py-1 rounded-full flex-shrink-0">
                    <span class="text-xs font-bold text-slate-600">{{ device.type }}</span>
                    <span class="text-slate-300" style="font-size:10px">›</span>
                    <span class="text-xs font-black text-slate-900 max-w-20 truncate">
                      {{ (device.interventionTypes.find(t => t.id === device.intervention_type_id) || {}).nom }}
                    </span>
                    <span v-if="device.duree_estimee" class="text-xs font-black text-orange-500">{{ formatDuration(device.duree_estimee) }}</span>
                  </div>
                </div>
                <div v-if="totalDuree" class="flex-shrink-0 flex items-center gap-1 border-l border-slate-200 pl-3">
                  <i class="fas fa-clock text-slate-400" style="font-size:10px"></i>
                  <span class="text-xs font-black text-slate-900">{{ formatDuration(totalDuree) }}</span>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════
         MODALE MOBILE : Choisir un type d'appareil
         ═══════════════════════════════════════════ -->
    <modal name="mobile-add-device" height="auto" :scrollable="true" :adaptive="true">
      <div class="p-10 overflow-y-auto max-h-[100vh]" style="z-index: 999 !important;">

        <!-- En-tête -->
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px;">
          <div>
            <p style="font-size: 17px; font-weight: 600; color: #1a1a1a;">Quel appareil ?</p>
            <p style="font-size: 13px; color: #888; margin-top: 2px;">Choisissez un type</p>
          </div>
          <button @click="$modal.hide('mobile-add-device')"
            style="width: 32px; height: 32px; border-radius: 50%; background: #f4f3f0; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer;">
            <i class="fas fa-xmark" style="font-size: 16px; color: #555;"></i>
          </button>
        </div>

        <!-- Chargement -->
        <div v-if="loadingCategories" class="flex items-center justify-center gap-3 py-8">
          <i class="fas fa-spinner fa-spin" style="color: #f97316; font-size: 20px;"></i>
          <span style="color: #888;">Chargement…</span>
        </div>

        <!-- Grille catégories -->
        <div v-else style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px;">
          <button v-for="cat in categories" :key="cat.id"
            @click="addDeviceAndOpenModal(cat)"
            style="background: #f4f3f0; border-radius: 16px; border: 1.5px solid #e8e6e1; padding: 18px 12px; display: flex; flex-direction: column; align-items: center; gap: 10px; cursor: pointer;">
            <div style="width: 50px; height: 50px; border-radius: 14px; background: #fff; display: flex; align-items: center; justify-content: center;">
              <img v-if="cat.icon" :src="cat.icon" :alt="cat.label" style="width: 26px; height: 26px; object-fit: contain;" />
              <i v-else class="fas fa-fire" style="font-size: 22px; color: #555;"></i>
            </div>
            <span style="font-size: 13px; font-weight: 600; color: #1a1a1a; text-align: center; line-height: 1.3;">{{ cat.label }}</span>
          </button>
        </div>

      </div>
    </modal>

    <!-- ═══════════════════════════════════════════
         MODALE MOBILE : Choisir une intervention
         ═══════════════════════════════════════════ -->
    <modal name="mobile-intervention" height="auto" :scrollable="true" :adaptive="true">
      <div class="p-10 overflow-y-auto max-h-[100vh]" style="z-index: 999 !important;">

        <template v-if="selectedDevice">

          <!-- En-tête appareil -->
          <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 20px;">
            <div style="width: 42px; height: 42px; border-radius: 12px; background: #f4f3f0; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img v-if="selectedDevice.icon" :src="selectedDevice.icon" :alt="selectedDevice.type" style="width: 22px; height: 22px; object-fit: contain;" />
              <i v-else class="fas fa-fire" style="font-size: 20px; color: #555;"></i>
            </div>
            <p style="font-size: 17px; font-weight: 600; color: #1a1a1a; flex: 1;">{{ selectedDevice.type }}</p>
            <button @click="$modal.hide('mobile-intervention')"
              style="width: 32px; height: 32px; border-radius: 50%; background: #f4f3f0; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0;">
              <i class="fas fa-xmark" style="font-size: 16px; color: #555;"></i>
            </button>
          </div>

          <!-- Déjà planifié -->
          <div v-if="selectedDevice.chantierInterventions && selectedDevice.chantierInterventions.length"
            style="display: flex; flex-direction: column; gap: 10px;">
            <div v-for="iv in selectedDevice.chantierInterventions" :key="iv.id"
              style="display: flex; align-items: center; gap: 12px; background: #eff6ff; border: 1.5px solid #93c5fd; border-radius: 16px; padding: 16px;">
              <i class="fas fa-calendar-check" style="font-size: 22px; color: #2563eb; flex-shrink: 0;"></i>
              <div style="flex: 1; min-width: 0;">
                <p style="font-size: 16px; font-weight: 600; color: #1a1a1a;">{{ (iv.intervention_type && iv.intervention_type.nom) || '—' }}</p>
                <p style="font-size: 13px; font-weight: 500; margin-top: 2px;" :style="{ color: iv.statut && iv.statut.couleur ? iv.statut.couleur : '#2563eb' }">
                  {{ iv.statut && iv.statut.libelle }}
                </p>
              </div>
            </div>
            <p style="font-size: 13px; color: #888; text-align: center; margin-top: 4px;">
              <i class="fas fa-lock" style="margin-right: 4px;"></i>Intervention déjà prévue sur ce chantier
            </p>
            <button @click="$modal.hide('mobile-intervention')"
              style="width: 100%; height: 50px; background: #1a1a1a; color: #fff; border: none; border-radius: 14px; font-size: 16px; font-weight: 600; cursor: pointer; margin-top: 4px;">
              Fermer
            </button>
          </div>

          <!-- Chargement types -->
          <div v-else-if="selectedDevice.loadingTypes" style="display: flex; align-items: center; justify-content: center; gap: 12px; padding: 24px 0;">
            <i class="fas fa-spinner fa-spin" style="font-size: 22px; color: #f97316;"></i>
            <span style="color: #888; font-size: 15px;">Chargement…</span>
          </div>

          <!-- Liste des types d'intervention -->
          <template v-else>
            <div style="display: flex; flex-direction: column; gap: 8px;">
              <button
                v-for="t in selectedDevice.interventionTypes"
                :key="t.id"
                @click="pickIntervention(t.id)"
                style="width: 100%; display: flex; align-items: center; gap: 12px; padding: 16px; border-radius: 14px; border: 1.5px solid #e8e6e1; text-align: left; cursor: pointer;"
                :style="selectedDevice.intervention_type_id === t.id
                  ? 'background: #fff7ed; border-color: #fdba74;'
                  : 'background: #fff;'">
                <div style="width: 24px; height: 24px; border-radius: 50%; border: 1.5px solid #ccc; display: flex; align-items: center; justify-content: center; flex-shrink: 0;"
                  :style="selectedDevice.intervention_type_id === t.id ? 'background: #f97316; border-color: #f97316;' : 'background: #fff;'">
                  <i v-if="selectedDevice.intervention_type_id === t.id" class="fas fa-check" style="font-size: 11px; color: #fff;"></i>
                </div>
                <div>
                  <p style="font-size: 15px; font-weight: 600;"
                    :style="selectedDevice.intervention_type_id === t.id ? 'color: #c2410c;' : 'color: #1a1a1a;'">{{ t.nom }}</p>
                  <p v-if="t.time_min" style="font-size: 13px; margin-top: 2px;"
                    :style="selectedDevice.intervention_type_id === t.id ? 'color: #ea580c;' : 'color: #888;'">
                    <i class="fas fa-clock" style="font-size: 11px; vertical-align: -1px; margin-right: 3px;"></i>{{ formatDuration(t.time_min) }}
                  </p>
                </div>
              </button>
            </div>

            <!-- Pied : Valider + Supprimer -->
            <div style="display: flex; gap: 10px; margin-top: 16px; padding-top: 16px; border-top: 1px solid #edecea;">
              <button @click="pickIntervention(selectedDevice.intervention_type_id)"
                :disabled="!selectedDevice.intervention_type_id"
                style="flex: 1; height: 52px; border: none; border-radius: 14px; font-size: 16px; font-weight: 600; cursor: pointer;"
                :style="selectedDevice.intervention_type_id
                  ? 'background: #f97316; color: #fff;'
                  : 'background: #e0deda; color: #aaa; cursor: default;'">
                Valider
              </button>
              <button @click="confirmDeleteDevice(selectedDeviceIndex); $modal.hide('mobile-intervention')"
                :disabled="loadingDeleteDevice"
                style="width: 52px; height: 52px; border-radius: 14px; background: #fff1f0; border: 1.5px solid #fca5a5; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0;">
                <i v-if="loadingDeleteDevice" class="fas fa-spinner fa-spin" style="font-size: 16px; color: #ef4444;"></i>
                <i v-else class="fas fa-trash" style="font-size: 16px; color: #ef4444;"></i>
              </button>
            </div>
          </template>

        </template>

      </div>
    </modal>

  </div>
</template>

<script>
let _uidCounter = 0

export default {
  name: 'AppareilsParc',

  props: {
    clientId:    { type: Number,  default: null },
    chantierId:  { type: Number,  default: null },
    readonly:    { type: Boolean, default: false },
    coordonnees: { type: Object,  default: null },
  },

  emits: ['change', 'device-deleted'],

  data() {
    return {
      categories: [],
      loadingCategories: false,
      addedDevices: [],
      loadingAddressDevices: false,
      addressDevicesLoaded: false,
      selectedDeviceIndex: null,
      deviceHistory: [],
      loadingHistory: false,
      loadingDeleteDevice: false,
      showAddDropdown: false,
      _historyCache: {},
    }
  },

  computed: {
    selectedDevice() {
      return this.selectedDeviceIndex !== null ? this.addedDevices[this.selectedDeviceIndex] : null
    },
    totalDuree() {
      return this.addedDevices.reduce((s, d) => s + (parseInt(d.duree_estimee) || 0), 0)
    },
    configuredDevices() {
      return this.addedDevices
        .filter(d => d.intervention_type_id)
        .map(d => ({
          appareil_id: d.appareil_id || null,
          app_type_id: d.app_type_id,
          type: d.key,
          intervention_type_id: d.intervention_type_id,
          duree_estimee: d.duree_estimee || null,
        }))
    },
  },

  watch: {
    clientId(val, old) {
      if (val === old || !val) return
      if (this.coordonnees && this.coordonnees.latitude && this.coordonnees.longitude) {
        // coordonnees déjà là → charger par adresse maintenant qu'on a le clientId
        this.loadDevicesAtAddress(this.coordonnees.longitude, this.coordonnees.latitude, val)
        return
      }
      this.loadDevicesForClient(val)
    },
    coordonnees(val, old) {
      if (!val || !val.latitude || !val.longitude || !this.clientId) return
      if (val.latitude === (old && old.latitude) && val.longitude === (old && old.longitude)) return
      this.loadDevicesAtAddress(val.longitude, val.latitude, this.clientId)
    },
    configuredDevices: { deep: true, handler(val) { this.$emit('change', val) } },
  },

  methods: {
    async loadDevicesAtAddress(longitude, latitude, clientId) {
      this.loadingAddressDevices = true
      this.addedDevices = []
      this.deviceHistory = []
      this.closeDevicePanel()
      this._historyCache = {}
      try {
        const payload = { longitude, latitude, client_id: clientId }
        if (this.chantierId) payload.chantier_id = this.chantierId
        const appareils = await this.$request.post('/client-appareils/get-by-adresse', payload) || []
        this.addedDevices = appareils.map(app => {
          const cat = this.categories.find(c => c.key === app.type)
          if (app.id && app.last_interventions) this._historyCache[app.id] = app.last_interventions
          return this._buildDevice(app, cat)
        })
        const needsTypes = this.addedDevices.filter(d => !d.interventionTypes.length && d.key)
        const uniqueKeys = [...new Set(needsTypes.map(d => d.key))]
        if (uniqueKeys.length) {
          const keyToTypes = {}
          await Promise.all(uniqueKeys.map(async key => {
            try { keyToTypes[key] = await this.$request.post('/intervention-types/list', { categorie: key }) || [] }
            catch { keyToTypes[key] = [] }
          }))
          this.addedDevices = this.addedDevices.map(d => ({
            ...d,
            interventionTypes: d.interventionTypes.length ? d.interventionTypes : (keyToTypes[d.key] || []),
            loadingTypes: false,
          }))
        }
        if (this.addedDevices.length > 0) {
          this.selectedDeviceIndex = 0
          this.deviceHistory = this._historyCache[this.addedDevices[0].appareil_id] || []
        }
      } catch (e) { console.error(e) }
      finally { this.loadingAddressDevices = false; this.addressDevicesLoaded = true }
    },

    async loadDevicesForClient(clientId) {
      this.loadingAddressDevices = true
      this.addedDevices = []
      this.closeDevicePanel()
      this._historyCache = {}
      try {
        const res = await this.$request.get('/client-appareils?client_id=' + clientId + '&all=1')
        const appareils = Array.isArray(res) ? res : (res && res.data ? res.data : [])
        this.addedDevices = appareils.map(app => {
          const cat = this.categories.find(c => c.key === app.type || c.label === app.type)
          return this._buildDevice(app, cat)
        })
        const keyToTypes = {}
        const uniqueKeys = [...new Set(this.addedDevices.map(d => d.key).filter(Boolean))]
        await Promise.all(uniqueKeys.map(async key => {
          try { keyToTypes[key] = await this.$request.post('/intervention-types/list', { categorie: key }) || [] }
          catch { keyToTypes[key] = [] }
        }))
        this.addedDevices = this.addedDevices.map(d => ({ ...d, interventionTypes: keyToTypes[d.key] || [], loadingTypes: false }))
        if (this.addedDevices.length > 0) {
          this.selectedDeviceIndex = 0
          if (this.addedDevices[0].appareil_id) this.loadDeviceHistory(this.addedDevices[0].appareil_id)
        }
      } catch (e) { console.error(e) }
      finally { this.loadingAddressDevices = false; this.addressDevicesLoaded = true }
    },

    async loadCategories() {
      this.loadingCategories = true
      try { this.categories = await this.$request.post('/reference/get-categories', {}) || [] }
      catch (e) { console.error(e) }
      finally { this.loadingCategories = false }
    },

    async addDevice(cat) {
      const device = {
        _uid: ++_uidCounter, appareil_id: null, app_type_id: cat.id,
        type: cat.label, key: cat.key, icon: cat.icon || null,
        marque: null, modele: null, numero_serie: null, emplacement: null,
        intervention_type_id: null, interventionTypes: [], duree_estimee: null,
        loadingTypes: true, chantierInterventions: [],
      }
      this.addedDevices.push(device)
      const idx = this.addedDevices.length - 1
      this.selectedDeviceIndex = idx
      this.deviceHistory = []
      await this._loadTypesForDevice(idx, cat.key)
    },

    async addDeviceAndOpenModal(cat) {
      this.$modal.hide('mobile-add-device')
      this.$modal.show('mobile-intervention')
      await this.addDevice(cat)
    },

    openInterventionModal(i) {
      this.selectDevice(i)
      this.$nextTick(() => this.$modal.show('mobile-intervention'))
    },

    pickIntervention(typeId) {
      this.setInterventionType(this.selectedDeviceIndex, typeId)
      this.$modal.hide('mobile-intervention')
    },

    adjustDuration(delta) {
      if (this.selectedDeviceIndex === null) return
      const device = this.addedDevices[this.selectedDeviceIndex]
      this.$set(this.addedDevices, this.selectedDeviceIndex, {
        ...device, duree_estimee: Math.max(15, (device.duree_estimee || 60) + delta)
      })
    },

    async confirmDeleteDevice(i) {
      const device = this.addedDevices[i]
      if (device.appareil_id) {
        if (!confirm('Supprimer définitivement "' + device.type + '" ?')) return
        this.loadingDeleteDevice = true
        try {
          await this.$request.post('/client-appareils/' + device.appareil_id + '/delete')
          this.$emit('device-deleted', { appareil_id: device.appareil_id })
        } catch {
          this.$toast && this.$toast.error('Erreur lors de la suppression.')
          this.loadingDeleteDevice = false; return
        }
        this.loadingDeleteDevice = false
      }
      this._removeDevice(i)
    },

    _removeDevice(i) {
      if (this.selectedDeviceIndex === i) {
        this.closeDevicePanel()
        this.addedDevices.splice(i, 1)
        if (this.addedDevices.length > 0) {
          const ni = Math.min(i, this.addedDevices.length - 1)
          this.selectedDeviceIndex = ni
          const d = this.addedDevices[ni]
          this.deviceHistory = d && d.appareil_id ? (this._historyCache[d.appareil_id] || []) : []
          if (d && d.appareil_id && !this._historyCache[d.appareil_id]) this.loadDeviceHistory(d.appareil_id)
        }
      } else {
        if (this.selectedDeviceIndex !== null && i < this.selectedDeviceIndex) this.selectedDeviceIndex--
        this.addedDevices.splice(i, 1)
      }
    },

    selectDevice(i) {
      if (this.selectedDeviceIndex === i) return
      this.selectedDeviceIndex = i
      const d = this.addedDevices[i]
      if (d && d.appareil_id && this._historyCache[d.appareil_id] !== undefined) {
        this.deviceHistory = this._historyCache[d.appareil_id]
      } else if (d && d.appareil_id) {
        this.deviceHistory = []
        this.loadDeviceHistory(d.appareil_id)
      } else {
        this.deviceHistory = []
      }
    },

    closeDevicePanel() { this.selectedDeviceIndex = null; this.deviceHistory = [] },

    setInterventionType(i, value) {
      const typeId = (value !== null && value !== '' && value !== undefined) ? parseInt(value) : null
      const device = this.addedDevices[i]
      const type = device.interventionTypes.find(t => t.id === typeId)
      this.$set(this.addedDevices, i, {
        ...device, intervention_type_id: typeId, duree_estimee: type && type.time_min ? type.time_min : null,
      })
    },

    setDureeEstimeeHHMM(i, heures, minutes) {
      const h = Math.max(0, parseInt(heures) || 0)
      const m = Math.min(59, Math.max(0, parseInt(minutes) || 0))
      this.$set(this.addedDevices, i, { ...this.addedDevices[i], duree_estimee: Math.max(15, h * 60 + m) })
    },

    getDefaultDuree(device) {
      if (!device || !device.intervention_type_id) return null
      const type = device.interventionTypes.find(t => t.id === device.intervention_type_id)
      return type && type.time_min ? type.time_min : null
    },

    resetDureeEstimee(i) {
      this.$set(this.addedDevices, i, { ...this.addedDevices[i], duree_estimee: this.getDefaultDuree(this.addedDevices[i]) })
    },

    async loadDeviceHistory(appareilId) {
      this.loadingHistory = true
      try {
        const res = await this.$request.get('/interventions', {
          params: { client_appareil_id: appareilId, per_page: 10, sort_by: 'date_intervention', sort_dir: 'desc' },
        })
        const data = (res && res.data ? res.data : res) || []
        this.deviceHistory = Array.isArray(data)
          ? data.filter(item => !item.client_appareil_id || item.client_appareil_id == appareilId).slice(0, 8)
          : []
        this._historyCache[appareilId] = this.deviceHistory
      } catch (e) { console.error(e) }
      finally { this.loadingHistory = false }
    },

    _buildDevice(app, cat) {
      return {
        _uid: ++_uidCounter,
        appareil_id: app.id || null,
        app_type_id: (cat && cat.id) || app.app_type_id || null,
        type: (cat && cat.label) || (app.app_type && app.app_type.label) || app.type,
        key: (cat && cat.key) || app.type,
        icon: (cat && cat.icon) || null,
        marque: app.marque || null, modele: app.model || null,
        numero_serie: app.numero_serie || null, emplacement: app.emplacement || null,
        intervention_type_id: null,
        interventionTypes: app.intervention_types || [],
        duree_estimee: null, loadingTypes: false,
        chantierInterventions: app.last_interventions || [],
      }
    },

    async _loadTypesForDevice(idx, categoryKey) {
      try {
        const types = await this.$request.post('/intervention-types/list', { categorie: categoryKey })
        this.$set(this.addedDevices, idx, { ...this.addedDevices[idx], interventionTypes: types || [], loadingTypes: false })
      } catch {
        this.$set(this.addedDevices, idx, { ...this.addedDevices[idx], loadingTypes: false })
      }
    },

    formatDuration(mins) {
      if (!mins) return ''
      const h = Math.floor(mins / 60), m = mins % 60
      return h ? (m ? h + 'h' + String(m).padStart(2, '0') : h + 'h') : m + 'min'
    },

    formatHistoryDate(d) {
      return d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
    },

    reset() {
      this.addedDevices = []
      this.selectedDeviceIndex = null
      this.deviceHistory = []
      this.addressDevicesLoaded = false
      this.loadingAddressDevices = false
      this._historyCache = {}
      this.showAddDropdown = false
  },
  },

  async mounted() {
    await this.loadCategories()
    if (this.coordonnees && this.coordonnees.latitude && this.coordonnees.longitude && this.clientId) {
      await this.loadDevicesAtAddress(this.coordonnees.longitude, this.coordonnees.latitude, this.clientId)
    } else if (this.clientId) {
      await this.loadDevicesForClient(this.clientId)
    }

    this.$eventHub.$on('reset-appareils-parc', this.reset)
  },
}
</script>